import React, { useMemo, useState } from "react";
import {
  useGame,
  usePlayer,
  usePlayers,
} from "@empirica/core/player/classic/react";
import { Button } from "../components/Button";
import { useRandomizedQuizQuestions } from "../components/useRandomizedQuizQuestions";
import {
  getCounterpartCopy,
  getNegotiationTermCopy,
  isLossFrameDesign,
} from "../payoffFrame";
import { Stage2PayAtStakeVisualization } from "../stage2/Stage2ResultStage";

const DEFAULT_NEGOTIATION_DISCUSSION_DURATION_SECONDS = 300;
const GAIN_DEMAND_NEGOTIATION_DIAGRAM_SRC = "/Negotiation%20Design.svg";
const LOSS_DEMAND_NEGOTIATION_DIAGRAM_SRC = "/Negotiation%20Design%20Loss.svg";
const GAIN_OFFER_NEGOTIATION_DIAGRAM_SRC = "/negotiation-offer-flow.png";
const LOSS_OFFER_NEGOTIATION_DIAGRAM_SRC = "/Negotiation%20Design%20Loss%20(Offer).svg";

function formatDuration(seconds) {
  const roundedSeconds = Math.round(Number(seconds));

  if (!Number.isFinite(roundedSeconds) || roundedSeconds <= 0) {
    return "the discussion time";
  }

  if (roundedSeconds < 60) {
    return `${roundedSeconds} ${roundedSeconds === 1 ? "second" : "seconds"}`;
  }

  const minutes = Math.floor(roundedSeconds / 60);
  const remainingSeconds = roundedSeconds % 60;
  const minuteLabel = `${minutes} ${minutes === 1 ? "minute" : "minutes"}`;

  if (remainingSeconds === 0) {
    return minuteLabel;
  }

  return `${minuteLabel} and ${remainingSeconds} ${
    remainingSeconds === 1 ? "second" : "seconds"
  }`;
}

function formatPercent(value) {
  const number = Number(value);

  if (!Number.isFinite(number)) {
    return "0";
  }

  return number.toFixed(1).replace(/\.0$/, "");
}

function getQuizQuestions(
  discussionDurationLabel,
  shrinkPercentLabel,
  lossFrame,
  counterpartLabel,
  termCopy
) {
  const combinedTerm = `${termCopy.term} and your ${counterpartLabel}'s ${termCopy.term}`;

  return [
    {
      id: "combinedDemand",
      prompt: lossFrame
        ? `What happens if your ${combinedTerm} add up to the Game 2 saved amount or less?`
        : `What happens if your ${combinedTerm} add up to the team winnings or less?`,
      options: lossFrame
        ? [
            {
              value: "random",
              label: "You both win the first round and move to the second round of negotiation.",
            },
            {
              value: "correct",
              label:
                "The negotiation succeeds, and any unclaimed savings are split evenly.",
            },
            {
              value: "winner",
              label: "Whoever tracked the circle longer avoids all losses.",
            },
          ]
        : [
            {
              value: "random",
              label: "You both win the first round and move to the second round of negotiation.",
            },
            {
              value: "correct",
              label:
                `Each person receives their ${termCopy.term}, and any unclaimed pay is split evenly.`,
            },
            {
              value: "winner",
              label: "Whoever tracked the circle longer gets all the pay.",
            },
          ],
      correctValue: "correct",
    },
    {
      id: "overDemand",
      prompt: lossFrame
        ? `What happens if your ${combinedTerm} add up to more than the Game 2 saved amount?`
        : `What happens if your ${combinedTerm} add up to more than the team winnings?`,
      options: lossFrame
        ? [
            {
              value: "receive",
              label: "The negotiation succeeds, and any unclaimed savings are split evenly.",
            },
            {
              value: "shrink",
              label: `The saved money shrinks by ${shrinkPercentLabel} and the two of you can revise your ${termCopy.pluralTerm}.`,
            },
            {
              value: "correct",
              label:
                `The two of you discuss with each other for ${discussionDurationLabel} and then can revise your ${termCopy.pluralTerm}.`,
            },
          ]
        : [
            {
              value: "receive",
              label: `Each person receives their ${termCopy.term}, and any unclaimed amount is split evenly.`,
            },
            {
              value: "shrink",
              label: `The team winnings shrink by ${shrinkPercentLabel} and the two of you can revise your ${termCopy.pluralTerm}.`,
            },
            {
              value: "correct",
              label:
                `The two of you discuss with each other for ${discussionDurationLabel} and then can revise your ${termCopy.pluralTerm}.`,
            },
          ],
      correctValue: "correct",
    },
    {
      id: "afterDiscussion",
      prompt: lossFrame
        ? `What happens if your ${combinedTerm} still add up to more than the Game 2 saved amount after revision?`
        : `What happens if your ${combinedTerm} add up to more than the team winnings after revision?`,
      options: lossFrame
        ? [
            {
              value: "correct",
              label: `The saved money shrinks by ${shrinkPercentLabel} and the remaining savings are split randomly.`,
            },
            {
              value: "even",
              label: `The loss is split evenly between you and your ${counterpartLabel}.`,
            },
            {
              value: "retry",
              label: "There is another round of revision.",
            },
          ]
        : [
            {
              value: "correct",
              label: `The team winnings shrink by ${shrinkPercentLabel} and are split randomly between you and your ${counterpartLabel}.`,
            },
            {
              value: "even",
              label: `The money is split evenly between you and your ${counterpartLabel}.`,
            },
            {
              value: "retry",
              label: "There is another round of revision.",
            },
          ],
      correctValue: "correct",
    },
    {
      id: "offerCount",
      prompt: `How many ${termCopy.pluralTerm} will you make during the negotiation?`,
      options: [
        {
          value: "two",
          label: `2 ${termCopy.pluralTerm}: one initial ${termCopy.term} and one final ${termCopy.term} after discussion.`,
        },
        {
          value: "three",
          label: `3 ${termCopy.pluralTerm}.`,
        },
        {
          value: "asNeeded",
          label: `As many ${termCopy.pluralTerm} as are needed.`,
        },
      ],
      correctValue: "two",
    },
  ];
}

export function NegotiationRulesStage() {
  const game = useGame();
  const player = usePlayer();
  const players = usePlayers();
  const stage2 = game.get("stage2");
  const stage1 = game.get("stage1") || {};
  const design = game.get("design") || {};
  const lossFrame = isLossFrameDesign(design);
  const termCopy = getNegotiationTermCopy(design);
  const counterpartLabel = getCounterpartCopy(design).term;
  const discussionDurationLabel = formatDuration(
    design.negotiationDiscussionDurationSeconds ??
      DEFAULT_NEGOTIATION_DISCUSSION_DURATION_SECONDS
  );
  const noAgreementShrinkFactor = Number(design.noAgreementShrinkFactor ?? 0.8);
  const shrinkPercent = Math.max(0, (1 - noAgreementShrinkFactor) * 100);
  const shrinkPercentLabel = `${formatPercent(shrinkPercent)}%`;
  const questions = useMemo(
    () =>
      getQuizQuestions(
        discussionDurationLabel,
        shrinkPercentLabel,
        lossFrame,
        counterpartLabel,
        termCopy
      ),
    [counterpartLabel, discussionDurationLabel, lossFrame, shrinkPercentLabel, termCopy]
  );
  const quizQuestions = useRandomizedQuizQuestions(
    questions, player.stage, "negotiationRulesQuizOptionOrder"
  );
  const [showDiagram, setShowDiagram] = useState(
    player.stage.get("negotiationDiagramShown") === true
  );
  const [currentPage, setCurrentPage] = useState(
    player.stage.get("negotiationRulesPage") === "quiz" ? "quiz" : "rules"
  );
  const [answers, setAnswers] = useState(
    player.stage.get("negotiationRulesQuizAnswers") ?? {}
  );
  const [submittedAnswers, setSubmittedAnswers] = useState(
    player.stage.get("negotiationRulesQuizSubmittedAnswers") ?? {}
  );
  const [attemptedQuiz, setAttemptedQuiz] = useState(
    player.stage.get("negotiationRulesQuizAttempted") === true
  );

  const allSubmitted = useMemo(
    () => players.every((p) => p.stage.get("submit")),
    [players]
  );
  const incorrectQuestionIds = useMemo(
    () =>
      quizQuestions.filter(
        (question) => submittedAnswers[question.id] !== question.correctValue
      ).map((question) => question.id),
    [quizQuestions, submittedAnswers]
  );
  const allQuestionsAnswered = useMemo(
    () =>
      quizQuestions.every(
        (question) => typeof submittedAnswers[question.id] === "string"
      ),
    [quizQuestions, submittedAnswers]
  );
  const passedQuiz = attemptedQuiz && incorrectQuestionIds.length === 0;

  function handleContinue() {
    player.stage.set("submit", true);
  }

  function handleShowDiagram() {
    if (!showDiagram) {
      setShowDiagram(true);
      player.stage.set("negotiationDiagramShown", true);
    }
  }

  function handleContinueToQuiz() {
    setCurrentPage("quiz");
    player.stage.set("negotiationRulesPage", "quiz");
  }

  function handleBackToRules() {
    setCurrentPage("rules");
    player.stage.set("negotiationRulesPage", "rules");
  }

  function handleAnswerChange(questionId, value) {
    const nextAnswers = {
      ...answers,
      [questionId]: value,
    };
    setAnswers(nextAnswers);
    player.stage.set("negotiationRulesQuizAnswers", nextAnswers);
  }

  function handleSubmitQuiz() {
    const nextCount = (player.stage.get("negotiationRulesQuizAttemptCount") ?? 0) + 1;
    setSubmittedAnswers(answers);
    setAttemptedQuiz(true);
    // Keep repeated submissions distinct in Empirica's attribute history.
    player.stage.set("negotiationRulesQuizSubmittedAnswers", { ...answers, _attempt: nextCount });
    player.stage.set("negotiationRulesQuizAttempted", true);
    player.stage.set("negotiationRulesQuizAttemptCount", nextCount);
  }

  if (!stage2) {
    return (
      <div className="max-w-3xl mx-auto px-8 py-6 text-center text-gray-500">
        Loading negotiation rules…
      </div>
    );
  }

  const teamWinningsBeforeAdjustment = Number(
    stage2.teamWinningsBeforeAdjustment ?? 0
  );
  const savedAmountLabel = `$${teamWinningsBeforeAdjustment.toFixed(2)}`;
  const negotiationPoolLabel = lossFrame ? "savings" : "winnings";
  const diagramSrc = termCopy.term === "offer"
    ? lossFrame
      ? LOSS_OFFER_NEGOTIATION_DIAGRAM_SRC
      : GAIN_OFFER_NEGOTIATION_DIAGRAM_SRC
    : lossFrame
      ? LOSS_DEMAND_NEGOTIATION_DIAGRAM_SRC
      : GAIN_DEMAND_NEGOTIATION_DIAGRAM_SRC;
  const diagramLabel = `${lossFrame ? "Loss" : "Gain"}-frame ${termCopy.term} negotiation diagram`;

  return (
    <div className="py-8 max-w-4xl mx-auto px-8 space-y-6">
      {currentPage === "rules" ? (
        <>
          <div className="rounded-md border border-gray-200 p-4 space-y-2 text-2xl text-gray-700">
            <div className="text-3xl font-medium text-gray-900">
              How to divide the <b>{savedAmountLabel}</b> Game 2 {negotiationPoolLabel}?
            </div>

          <div>
            You and your {counterpartLabel} will divide the <b>{savedAmountLabel}</b> Game 2 {negotiationPoolLabel} in up to two rounds of negotiation.
          </div>

            <div>
              <span className="block">
                You make an <b>initial {termCopy.term}</b> of how much of the{" "}
                <b>{savedAmountLabel}</b> {negotiationPoolLabel} you want.
              </span>
              <span className="block">Your {counterpartLabel} does the same.</span>
            </div>
            <div>
              <span className="block">
                If <b>your {termCopy.term} + your {counterpartLabel}'s {termCopy.term}</b> is <b>{savedAmountLabel}</b>{" "}
                or <b>less</b>:
              </span>
              <span className="block pl-8">
                The negotiation ends. You both <b>receive your {termCopy.pluralTerm}</b> and unclaimed {negotiationPoolLabel} are split evenly.
              </span>
            </div>
            <div>
              <span className="block">
                If <b>your {termCopy.term} + your {counterpartLabel}'s {termCopy.term}</b> is <b>more</b> than <b>{savedAmountLabel}</b>: 
              </span>
              <span className="block pl-8">
                You and your {counterpartLabel} can <b>briefly discuss</b> over text before you each submit a <b>final {termCopy.term}</b>.
              </span>                
                
            </div>
            
            <div>
              The <b>final {termCopy.term}</b> works the same as the initial {termCopy.term}, except that if <b>your final {termCopy.term} + your {counterpartLabel}'s final {termCopy.term}</b> is <b>still greater</b> than <b>{savedAmountLabel}</b>, <b>there is <u>not</u> another round</b> of negotiation.
            </div>

            <div>
              Instead, the <b>{savedAmountLabel}</b> {negotiationPoolLabel} <b>shrink</b> by <b>{shrinkPercentLabel}</b> and are split <b>randomly</b> between you and your {counterpartLabel}.
            </div>

            <div className="text-2xl pt-2 font-medium text-gray-900">Diagram</div>
            <Button handleClick={handleShowDiagram} disabled={showDiagram}>
              {showDiagram ? "Diagram shown" : "Show Diagram"}
            </Button>
            {showDiagram ? (
              <div className="rounded-md border border-gray-200 bg-white p-3">
                <img
                  src={diagramSrc}
                  alt={diagramLabel}
                  className="w-full h-auto rounded border border-gray-100"
                />
              </div>
            ) : null}
          </div>

          {lossFrame ? (
            <Stage2PayAtStakeVisualization
              stage1={stage1}
              stage2={stage2}
              design={design}
              player={player}
              players={players}
            />
          ) : null}

          <div className="flex items-center space-x-4">
            <Button
              handleClick={handleContinueToQuiz}
              autoFocus
              disabled={player.stage.get("submit") || !showDiagram}
            >
              Continue
            </Button>
            {!showDiagram ? (
              <span className="text-sm text-gray-500">
                Press Show Diagram to continue.
              </span>
            ) : null}
          </div>
        </>
      ) : (
        <>
          <div className="rounded-md border border-gray-200 p-5 space-y-6 text-lg text-gray-700">
            <div className="space-y-2">
              <h2 className="text-2xl font-medium text-gray-900">
                Negotiation Rules Check
              </h2>
              <p>
                Answer all questions correctly to confirm you understand how the
                negotiation works.
              </p>
            </div>

            <div className="space-y-6">
              {quizQuestions.map((question, index) => {
                const selectedValue = answers[question.id];
                const submittedValue = submittedAnswers[question.id];
                const showIncorrect =
                  attemptedQuiz &&
                  typeof submittedValue === "string" &&
                  submittedValue !== question.correctValue;
                const showCorrect =
                  attemptedQuiz && submittedValue === question.correctValue;

                return (
                  <div
                    key={question.id}
                    className={`rounded-md border p-4 space-y-3 ${
                      showIncorrect
                        ? "border-red-300 bg-red-50"
                        : showCorrect
                          ? "border-green-300 bg-green-50"
                          : "border-gray-200"
                    }`}
                  >
                    <p className="font-medium text-gray-900">
                      {index + 1}. {question.prompt}
                    </p>
                    <div className="space-y-2">
                      {question.options.map((option) => (
                        <label
                          key={option.value}
                          className="flex items-center space-x-3 cursor-pointer"
                        >
                          <input
                            type="radio"
                            name={question.id}
                            value={option.value}
                            checked={selectedValue === option.value}
                            onChange={() =>
                              handleAnswerChange(question.id, option.value)
                            }
                            className="h-4 w-4 shrink-0"
                          />
                          <span>{option.label}</span>
                        </label>
                      ))}
                    </div>
                    {showIncorrect ? (
                      <p className="text-lg text-red-600">
                        This answer is not correct. Re-read the rules and try again.
                      </p>
                    ) : null}
                    {showCorrect ? (
                      <p className="text-lg text-green-700">Correct.</p>
                    ) : null}
                  </div>
                );
              })}
            </div>

            {attemptedQuiz && !passedQuiz ? (
              <div className="rounded-md border border-red-200 bg-red-50 p-4 text-lg text-red-700">
                {allQuestionsAnswered
                  ? "Some answers are still incorrect. Update your choices and submit the quiz again."
                  : "Answer every question, then submit the quiz again."}
              </div>
            ) : null}

            {passedQuiz ? (
              <div className="rounded-md border border-green-200 bg-green-50 p-4 text-lg text-green-700">
                All answers are correct. You can continue.
              </div>
            ) : null}
          </div>

          <div className="flex items-center space-x-4">
            <Button handleClick={handleBackToRules}>Back to Rules</Button>
            <Button
              handleClick={handleSubmitQuiz}
              disabled={player.stage.get("submit")}
            >
              Submit Quiz
            </Button>
            <Button
              handleClick={handleContinue}
              autoFocus
              disabled={player.stage.get("submit") || !passedQuiz}
            >
              Continue
            </Button>
            {player.stage.get("submit") && !allSubmitted ? (
              <span className="text-base text-gray-500">
                Waiting for {counterpartLabel}…
              </span>
            ) : null}
          </div>
        </>
      )}
    </div>
  );
}
